import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const rp = (n) => "Rp" + (n == null ? "0" : Math.round(n).toLocaleString("id-ID"));
const FIELDS = [["date", "Tanggal"], ["description", "Keterangan"], ["debit", "Debit (keluar)"], ["credit", "Kredit (masuk)"], ["reference", "Referensi"], ["balance", "Saldo"]];

function parseCSV(text) {
  const t = text.trim();
  if (!t) return { headers: [], rows: [] };
  const delim = t.includes("\t") ? "\t" : t.split("\n")[0].includes(";") ? ";" : ",";
  const split = (line) => {
    const out = []; let cur = ""; let q = false;
    for (const ch of line) {
      if (ch === '"') q = !q;
      else if (ch === delim && !q) { out.push(cur); cur = ""; }
      else cur += ch;
    }
    out.push(cur); return out.map((x) => x.trim().replace(/^"|"$/g, ""));
  };
  const lines = t.split(/\r?\n/).filter((l) => l.trim());
  const headers = split(lines[0]);
  const rows = lines.slice(1).map(split);
  return { headers, rows };
}

export default function BankReconciliation() {
  const { can } = useAuth();
  const canManage = can("finance.cashbank.manage");
  const [accounts, setAccounts] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [detail, setDetail] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [nf, setNf] = useState({ account_code: "", name: "", statement_start: "", statement_end: "", opening_balance: "0", closing_balance: "0" });

  const loadSessions = useCallback(async () => {
    try { setSessions((await api.get("/finance/bank-recon/sessions")).data); } catch (e) { toast.error(apiError(e)); }
  }, []);
  useEffect(() => {
    loadSessions();
    api.get("/finance/bank-recon/accounts").then((r) => setAccounts(r.data)).catch(() => {});
  }, [loadSessions]);

  const openDetail = async (id) => {
    try { setDetail((await api.get(`/finance/bank-recon/sessions/${id}`)).data); } catch (e) { toast.error(apiError(e)); }
  };
  const create = async () => {
    if (!nf.account_code || !nf.name || !nf.statement_start || !nf.statement_end) return toast.error("Lengkapi data sesi");
    try {
      const s = (await api.post("/finance/bank-recon/sessions", {
        ...nf, opening_balance: Number(nf.opening_balance || 0), closing_balance: Number(nf.closing_balance || 0),
      })).data;
      toast.success("Sesi rekonsiliasi dibuat");
      setShowNew(false); setNf({ account_code: "", name: "", statement_start: "", statement_end: "", opening_balance: "0", closing_balance: "0" });
      await loadSessions(); openDetail(s.id);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="p-6" data-testid="bank-recon-page">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-white">Rekonsiliasi Bank</h1>
          <p className="text-slate-400 text-sm">Cocokkan mutasi bank dengan Buku Besar (jurnal Posted). Penyesuaian membuat jurnal DRAFT untuk direview Accounting.</p>
        </div>
        {!detail && canManage && <Button data-testid="new-recon-btn" onClick={() => setShowNew(!showNew)} className="bg-indigo-600 hover:bg-indigo-500">+ Sesi Baru</Button>}
        {detail && <Button onClick={() => { setDetail(null); loadSessions(); }} className="bg-slate-700 hover:bg-slate-600">Kembali</Button>}
      </div>

      {!detail && showNew && (
        <Card className="p-4 bg-slate-900 border-slate-800 mb-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <div><label className="text-xs text-slate-400">Akun Bank</label>
              <select data-testid="recon-account" value={nf.account_code} onChange={(e) => setNf({ ...nf, account_code: e.target.value })} className="block w-full bg-slate-800 border border-slate-700 rounded px-2 py-2 text-sm text-white">
                <option value="">Pilih akun…</option>
                {accounts.map((a) => <option key={a.account_code} value={a.account_code}>{a.account_code} · {a.name} (GL {rp(a.gl_balance)})</option>)}
              </select></div>
            <div><label className="text-xs text-slate-400">Nama Sesi</label><Input data-testid="recon-name" value={nf.name} onChange={(e) => setNf({ ...nf, name: e.target.value })} placeholder="Rekonsiliasi BCA Juni" className="bg-slate-800 border-slate-700 text-white" /></div>
            <div><label className="text-xs text-slate-400">Saldo Awal (statement)</label><Input type="number" value={nf.opening_balance} onChange={(e) => setNf({ ...nf, opening_balance: e.target.value })} className="bg-slate-800 border-slate-700 text-white" /></div>
            <div><label className="text-xs text-slate-400">Tgl Awal</label><Input data-testid="recon-start" type="date" value={nf.statement_start} onChange={(e) => setNf({ ...nf, statement_start: e.target.value })} className="bg-slate-800 border-slate-700 text-white" /></div>
            <div><label className="text-xs text-slate-400">Tgl Akhir</label><Input data-testid="recon-end" type="date" value={nf.statement_end} onChange={(e) => setNf({ ...nf, statement_end: e.target.value })} className="bg-slate-800 border-slate-700 text-white" /></div>
            <div><label className="text-xs text-slate-400">Saldo Akhir (statement)</label><Input type="number" value={nf.closing_balance} onChange={(e) => setNf({ ...nf, closing_balance: e.target.value })} className="bg-slate-800 border-slate-700 text-white" /></div>
          </div>
          <div className="mt-3"><Button data-testid="save-recon-btn" onClick={create} className="bg-emerald-600 hover:bg-emerald-500">Simpan Sesi</Button></div>
        </Card>
      )}

      {!detail && (
        <Card className="p-0 bg-slate-900 border-slate-800 overflow-x-auto">
          <table className="w-full text-sm text-slate-300">
            <thead><tr className="text-left text-slate-500 border-b border-slate-800"><th className="p-3">Sesi</th><th>Akun</th><th>Periode</th><th>Status</th><th>Dibuat</th><th>Aksi</th></tr></thead>
            <tbody>
              {sessions.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-slate-500">Belum ada sesi rekonsiliasi.</td></tr>}
              {sessions.map((s) => (
                <tr key={s.id} data-testid={`recon-row-${s.id}`} className="border-b border-slate-800">
                  <td className="p-3">{s.name}</td><td>{s.account_code} · {s.account_name}</td>
                  <td className="text-xs">{s.statement_start} → {s.statement_end}</td>
                  <td><span className={`text-xs px-2 py-1 rounded ${s.status === "Selesai" ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-700/40 text-slate-300"}`}>{s.status}</span></td>
                  <td className="text-xs text-slate-500">{s.created_by}</td>
                  <td><Button data-testid={`open-recon-${s.id}`} onClick={() => openDetail(s.id)} className="h-7 px-2 bg-slate-700 hover:bg-slate-600 text-xs">Buka</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {detail && <ReconDetail detail={detail} reload={() => openDetail(detail.session.id)} accounts={accounts} canManage={canManage} />}
    </div>
  );
}

function ReconDetail({ detail, reload, accounts, canManage }) {
  const { session: s, statement_lines: stmt, gl_entries: gl, summary } = detail;
  const [csv, setCsv] = useState("");
  const [parsed, setParsed] = useState(null);
  const [map, setMap] = useState({ date: "", description: "", debit: "", credit: "", reference: "", balance: "" });
  const [templates, setTemplates] = useState([]);
  const [coa, setCoa] = useState([]);
  const [selStmt, setSelStmt] = useState("");
  const [selGl, setSelGl] = useState("");
  const [adj, setAdj] = useState({ line: null, counter: "", desc: "" });

  useEffect(() => {
    api.get("/finance/bank-recon/templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/accounting/chart-of-accounts").then((r) => setCoa(r.data.filter((a) => a.allow_posting))).catch(() => {});
  }, []);

  const doParse = () => {
    const p = parseCSV(csv);
    if (!p.headers.length) return toast.error("CSV kosong / tidak terbaca");
    setParsed(p);
    // auto map by header name
    const guess = {};
    const low = p.headers.map((h) => h.toLowerCase());
    const pick = (keys) => { const i = low.findIndex((h) => keys.some((k) => h.includes(k))); return i >= 0 ? p.headers[i] : ""; };
    guess.date = pick(["tanggal", "date", "tgl"]); guess.description = pick(["keterangan", "description", "uraian", "desc"]);
    guess.debit = pick(["debit", "debet", "keluar"]); guess.credit = pick(["kredit", "credit", "masuk"]);
    guess.reference = pick(["ref", "no"]); guess.balance = pick(["saldo", "balance"]);
    setMap({ ...map, ...guess });
  };

  const applyTemplate = (bank) => {
    const t = templates.find((x) => x.bank_name === bank);
    if (t) { setMap({ date: "", description: "", debit: "", credit: "", reference: "", balance: "", ...t.mapping }); toast.success(`Template ${bank} diterapkan`); }
  };
  const saveTemplate = async () => {
    const bank = prompt("Simpan mapping sebagai template bank (nama):");
    if (!bank) return;
    try { await api.post("/finance/bank-recon/templates", { bank_name: bank, mapping: map }); toast.success("Template disimpan"); api.get("/finance/bank-recon/templates").then((r) => setTemplates(r.data)); }
    catch (e) { toast.error(apiError(e)); }
  };

  const doImport = async () => {
    if (!parsed) return toast.error("Parse CSV dulu");
    if (!map.date) return toast.error("Kolom Tanggal wajib dipetakan");
    const idx = (f) => parsed.headers.indexOf(map[f]);
    const num = (v) => { const n = Number(String(v || "").replace(/[.,]/g, (m, o, s) => (s.lastIndexOf(",") === o ? "." : ""))); return isNaN(n) ? Number(String(v || "").replace(/[^0-9.-]/g, "")) || 0 : n; };
    const lines = parsed.rows.map((r) => ({
      date: r[idx("date")], description: map.description ? r[idx("description")] : "",
      debit: map.debit ? num(r[idx("debit")]) : 0, credit: map.credit ? num(r[idx("credit")]) : 0,
      reference: map.reference ? r[idx("reference")] : "", balance: map.balance ? num(r[idx("balance")]) : null,
    })).filter((l) => l.date);
    try { const res = (await api.post(`/finance/bank-recon/sessions/${s.id}/import`, { lines })).data; toast.success(`${res.imported} baris diimpor`); setCsv(""); setParsed(null); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const autoMatch = async () => { try { const r = (await api.post(`/finance/bank-recon/sessions/${s.id}/auto-match`)).data; toast.success(`${r.matched} baris tercocokkan otomatis`); reload(); } catch (e) { toast.error(apiError(e)); } };
  const doMatch = async () => {
    if (!selStmt || !selGl) return toast.error("Pilih baris mutasi & entri buku besar");
    try { await api.post(`/finance/bank-recon/sessions/${s.id}/match`, { statement_line_id: selStmt, gl_id: selGl }); toast.success("Dicocokkan"); setSelStmt(""); setSelGl(""); reload(); } catch (e) { toast.error(apiError(e)); }
  };
  const doUnmatch = async (id) => { try { await api.post(`/finance/bank-recon/sessions/${s.id}/unmatch`, { statement_line_id: id }); reload(); } catch (e) { toast.error(apiError(e)); } };
  const doAdjust = async () => {
    if (!adj.counter) return toast.error("Pilih akun lawan");
    try { const r = (await api.post(`/finance/bank-recon/sessions/${s.id}/adjustment`, { statement_line_id: adj.line, counter_account_code: adj.counter, description: adj.desc })).data; toast.success(`Jurnal Draft ${r.journal_no} dibuat`); setAdj({ line: null, counter: "", desc: "" }); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const finalize = async (reopen) => { try { await api.post(`/finance/bank-recon/sessions/${s.id}/finalize`, { reopen }); toast.success(reopen ? "Sesi dibuka kembali" : "Sesi diselesaikan"); reload(); } catch (e) { toast.error(apiError(e)); } };

  const glUnmatched = gl.filter((g) => !g.matched);

  return (
    <div className="space-y-4" data-testid="recon-detail">
      <div className="flex justify-between items-center">
        <h3 className="text-white font-semibold">{s.name} · {s.account_code} {s.account_name} · {s.statement_start}→{s.statement_end}
          <span className={`ml-2 text-xs px-2 py-1 rounded ${s.status === "Selesai" ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-700/40 text-slate-300"}`}>{s.status}</span></h3>
        {canManage && (s.status === "Selesai"
          ? <Button data-testid="reopen-recon-btn" onClick={() => finalize(true)} className="h-8 px-3 bg-amber-600 hover:bg-amber-500 text-xs">Buka Kembali</Button>
          : <Button data-testid="finalize-recon-btn" onClick={() => finalize(false)} className="h-8 px-3 bg-emerald-600 hover:bg-emerald-500 text-xs">Selesaikan</Button>)}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="p-3 bg-slate-900 border-slate-800"><div className="text-xs text-slate-400">Mutasi Masuk</div><div className="text-lg text-emerald-400">{rp(summary.statement_in)}</div></Card>
        <Card className="p-3 bg-slate-900 border-slate-800"><div className="text-xs text-slate-400">Mutasi Keluar</div><div className="text-lg text-rose-400">{rp(summary.statement_out)}</div></Card>
        <Card className="p-3 bg-slate-900 border-slate-800"><div className="text-xs text-slate-400">Net Statement</div><div className="text-lg text-white">{rp(summary.statement_net)}</div></Card>
        <Card className="p-3 bg-slate-900 border-slate-800"><div className="text-xs text-slate-400">Net Buku Besar</div><div className="text-lg text-white">{rp(summary.gl_movement_net)}</div></Card>
        <Card className="p-3 bg-slate-900 border-slate-800" data-testid="recon-diff"><div className="text-xs text-slate-400">Selisih</div><div className={`text-lg ${summary.reconciled ? "text-emerald-400" : "text-amber-400"}`}>{rp(summary.difference)}</div><div className="text-xs text-slate-500">{summary.reconciled ? "Terekonsiliasi" : `${summary.unmatched_statement_count} mutasi / ${summary.unmatched_gl_count} GL belum cocok`}</div></Card>
      </div>

      {canManage && s.status !== "Selesai" && (
        <Card className="p-4 bg-slate-900 border-slate-800">
          <div className="flex items-center justify-between mb-2"><div className="text-white text-sm font-medium">Impor Mutasi (CSV / paste)</div>
            <div className="flex gap-2 items-center">
              <select data-testid="template-select" onChange={(e) => applyTemplate(e.target.value)} className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-white">
                <option value="">Template bank…</option>{templates.map((t) => <option key={t.bank_name} value={t.bank_name}>{t.bank_name}</option>)}
              </select>
              <button onClick={saveTemplate} className="text-xs text-indigo-400">Simpan template</button>
            </div>
          </div>
          <textarea data-testid="csv-input" value={csv} onChange={(e) => setCsv(e.target.value)} rows={4} placeholder="Tanggal,Keterangan,Debit,Kredit,Saldo&#10;2026-06-01,Biaya Admin,6500,0,..." className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-2 text-xs text-white font-mono" />
          <div className="flex gap-2 mt-2"><Button data-testid="parse-csv-btn" onClick={doParse} className="h-8 px-3 bg-slate-700 hover:bg-slate-600 text-xs">Parse</Button>
            {parsed && <Button data-testid="import-csv-btn" onClick={doImport} className="h-8 px-3 bg-emerald-600 hover:bg-emerald-500 text-xs">Impor {parsed.rows.length} baris</Button>}</div>
          {parsed && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-3">
              {FIELDS.map(([f, label]) => (
                <div key={f}><label className="text-xs text-slate-400">{label}{f === "date" && " *"}</label>
                  <select data-testid={`map-${f}`} value={map[f]} onChange={(e) => setMap({ ...map, [f]: e.target.value })} className="block w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-white">
                    <option value="">—</option>{parsed.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                  </select></div>
              ))}
            </div>
          )}
        </Card>
      )}

      {canManage && s.status !== "Selesai" && (
        <div className="flex flex-wrap gap-2 items-end p-3 rounded bg-slate-800/40">
          <Button data-testid="auto-match-btn" onClick={autoMatch} className="h-9 px-3 bg-indigo-600 hover:bg-indigo-500 text-xs">Cocokkan Otomatis</Button>
          <div><label className="text-xs text-slate-400">Baris Mutasi (belum cocok)</label>
            <select data-testid="sel-stmt" value={selStmt} onChange={(e) => setSelStmt(e.target.value)} className="block bg-slate-800 border border-slate-700 rounded px-2 py-2 text-xs text-white w-72">
              <option value="">—</option>{stmt.filter((l) => !l.matched).map((l) => <option key={l.id} value={l.id}>{l.date} · {l.description} · {rp(l.credit - l.debit)}</option>)}
            </select></div>
          <div><label className="text-xs text-slate-400">Entri Buku Besar (belum cocok)</label>
            <select data-testid="sel-gl" value={selGl} onChange={(e) => setSelGl(e.target.value)} className="block bg-slate-800 border border-slate-700 rounded px-2 py-2 text-xs text-white w-72">
              <option value="">—</option>{glUnmatched.map((g) => <option key={g.gl_id} value={g.gl_id}>{g.date} · {g.journal_no} · {rp(g.signed)}</option>)}
            </select></div>
          <Button data-testid="manual-match-btn" onClick={doMatch} className="h-9 px-3 bg-emerald-600 hover:bg-emerald-500 text-xs">Cocokkan Manual</Button>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-0 bg-slate-900 border-slate-800 overflow-x-auto">
          <div className="p-3 text-white text-sm font-medium border-b border-slate-800">Mutasi Bank ({stmt.length})</div>
          <table className="w-full text-xs text-slate-300">
            <thead><tr className="text-left text-slate-500 border-b border-slate-800"><th className="p-2">Tgl</th><th>Keterangan</th><th className="text-right">Masuk</th><th className="text-right">Keluar</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {stmt.length === 0 && <tr><td colSpan="6" className="p-4 text-center text-slate-500">Belum ada mutasi.</td></tr>}
              {stmt.map((l) => (
                <tr key={l.id} data-testid={`stmt-line-${l.id}`} className="border-b border-slate-800">
                  <td className="p-2">{l.date}</td><td className="max-w-[160px] truncate">{l.description}</td>
                  <td className="text-right text-emerald-400">{l.credit ? rp(l.credit) : "-"}</td>
                  <td className="text-right text-rose-400">{l.debit ? rp(l.debit) : "-"}</td>
                  <td>{l.matched ? <span className="text-xs text-emerald-400">✓ {l.match_type}</span> : <span className="text-xs text-slate-500">belum</span>}</td>
                  <td>{canManage && s.status !== "Selesai" && (l.matched
                    ? (l.match_type !== "adjustment" && <button onClick={() => doUnmatch(l.id)} className="text-xs text-amber-400">batal</button>)
                    : <button data-testid={`adjust-${l.id}`} onClick={() => setAdj({ line: l.id, counter: "", desc: l.description })} className="text-xs text-indigo-400">jurnal</button>)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
        <Card className="p-0 bg-slate-900 border-slate-800 overflow-x-auto">
          <div className="p-3 text-white text-sm font-medium border-b border-slate-800">Buku Besar — {s.account_code} ({gl.length})</div>
          <table className="w-full text-xs text-slate-300">
            <thead><tr className="text-left text-slate-500 border-b border-slate-800"><th className="p-2">Tgl</th><th>Jurnal</th><th>Deskripsi</th><th className="text-right">Nilai</th><th>Status</th></tr></thead>
            <tbody>
              {gl.length === 0 && <tr><td colSpan="5" className="p-4 text-center text-slate-500">Tidak ada entri Posted pada periode.</td></tr>}
              {gl.map((g) => (
                <tr key={g.gl_id} className="border-b border-slate-800">
                  <td className="p-2">{g.date}</td><td>{g.journal_no}</td><td className="max-w-[140px] truncate">{g.description}</td>
                  <td className={`text-right ${g.signed >= 0 ? "text-emerald-400" : "text-rose-400"}`}>{rp(g.signed)}</td>
                  <td>{g.matched ? <span className="text-xs text-emerald-400">✓</span> : <span className="text-xs text-slate-500">belum</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>

      {adj.line && (
        <Card className="p-4 bg-slate-900 border-slate-800" data-testid="adjust-dialog">
          <div className="text-white text-sm font-medium mb-2">Jurnal Penyesuaian (DRAFT) untuk mutasi terpilih</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div><label className="text-xs text-slate-400">Akun Lawan</label>
              <select data-testid="adjust-counter" value={adj.counter} onChange={(e) => setAdj({ ...adj, counter: e.target.value })} className="block w-full bg-slate-800 border border-slate-700 rounded px-2 py-2 text-sm text-white">
                <option value="">Pilih akun…</option>{coa.map((a) => <option key={a.code} value={a.code}>{a.code} · {a.name}</option>)}
              </select></div>
            <div><label className="text-xs text-slate-400">Deskripsi</label><Input value={adj.desc} onChange={(e) => setAdj({ ...adj, desc: e.target.value })} className="bg-slate-800 border-slate-700 text-white" /></div>
            <div className="flex gap-2"><Button data-testid="save-adjust-btn" onClick={doAdjust} className="bg-emerald-600 hover:bg-emerald-500">Buat Jurnal Draft</Button>
              <Button onClick={() => setAdj({ line: null, counter: "", desc: "" })} className="bg-slate-700 hover:bg-slate-600">Batal</Button></div>
          </div>
          <p className="text-xs text-slate-500 mt-2">Jurnal dibuat berstatus DRAFT. Posting dilakukan oleh Accounting. Mutasi akan ditandai tercocok (adjustment).</p>
        </Card>
      )}
    </div>
  );
}
